import {Component, ElementRef, ViewChild} from '@angular/core';
import {DemandePermisVisiteService} from '../../services/demande-permis-visite.service';
import {AlertService} from '@shared/service/alert.service';
import {formatDate} from '@angular/common';
import {LocalService} from '@shared/storage/local.service';
import {StatutDemande} from '../../models/enumerations/statut-demande.model';
import {NavigationTunnelService} from '../../services/navigationTunnel.service';
import {MajDemandePermisVisiteSortiParcours} from '../../models/maj-demande-permis-visite-sorti-parcours.model';
import {IDemandePermisVisite} from '../../models/demande-permis-visite.model';
import { TranslateModule } from '@ngx-translate/core';
import {LogoMarianneComponent} from "@shared/logo-marianne/logo-marianne.component";

@Component({
    selector: 'ned-popin-sorti-demande-pv',
    templateUrl: './popin-sorti-dpv.component.html',
    styleUrls: ['./popin-sorti-dpv.component.scss'],
    standalone: true,
    imports: [TranslateModule, LogoMarianneComponent],
})
export class PopinSortiDpvComponent {

    @ViewChild('dialog') dialog!: ElementRef<HTMLDialogElement>;

    messagePopIn = '';

    private opener: HTMLElement | null = null;
    private _resolver!: (value: boolean) => void;

    result: Promise<boolean> = new Promise(resolve => this._resolver = resolve);

    constructor(private demandePermisVisiteService: DemandePermisVisiteService,
                private alertService: AlertService,
                private localService: LocalService,
                private navigationTunnelService: NavigationTunnelService) {
    }

    open() {
        this.opener = document.activeElement as HTMLElement;

        const dlg = this.dialog.nativeElement;
        dlg.showModal();

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
    }

    close(value: boolean) {
        const dlg = this.dialog.nativeElement;
        dlg.close();

        this.opener?.focus();
        this._resolver(value);

        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
    }

    /**
     * Méthode se déclenchant lorsque l'utilisateur
     * clique sur le bouton non de la pop in
     */
    reponseNonQuestionPopIn() {
        if (this.demandePermisVisiteService.isDemandeNonNull()) {
            this.demandePermisVisiteService.getDemande()!.demandeBrouillonEnCoursDeReprise = this.demandePermisVisiteService.getDemandeBrouillonEnCoursDeReprise();
        }
        // Si la demande est au statut brouillon et que l'utilisateur ne souhaite pas enregistrer les données saisies
        if (this.demandePermisVisiteService.isDemandeNonNull()
            && this.demandePermisVisiteService.getDemande()!.id
            && this.demandePermisVisiteService.getDemande()!.demandeBrouillonEnCoursDeReprise) {
            // L'utilisateur est sur une reprise et ne souhaite pas enregistrer les données saisies (restauration de la demande)
            this.gestionDuCasDeLaDemandeBrouillon(false);
        } else if (this.demandePermisVisiteService.isDemandeNonNull()
            && this.demandePermisVisiteService.getDemande()!.id
            && !this.demandePermisVisiteService.getDemande()!.demandeBrouillonEnCoursDeReprise
            && this.demandePermisVisiteService.getDemande()!.statut !== StatutDemande.A_COMPLETER) {
            // la première saisie de sa demande qui a déjà été enregistrée en BDD et ne souhaite pas enregistrer les données saisies (suppression de la demande)
            const idDemande = this.demandePermisVisiteService.getDemande()!.id;
            idDemande && this.demandePermisVisiteService.delete(idDemande).subscribe({
                next: success => {
                    this.sortiePopIn();
                },
                error: error => {
                    this.alertService.error('error.internalServer');
                },
            });
        } else {
            this.close(false);
        }
    }

    /**
     * Méthode se déclenchant lorsque l'utilisateur
     * clique sur le bouton oui de la pop in
     */
    reponseOuiQuestionPopIn() {
        if (this.demandePermisVisiteService.isDemandeNonNull()) {
            this.demandePermisVisiteService.getDemande()!.demandeBrouillonEnCoursDeReprise = this.demandePermisVisiteService.getDemandeBrouillonEnCoursDeReprise();
        }
        // Si la demande est au statut brouillon et que l'utilisateur souhaite enregistrer les données saisies
        if (this.demandePermisVisiteService.isDemandeNonNull()
            && this.demandePermisVisiteService.getDemande()!.id !== undefined
            && this.demandePermisVisiteService.getDemande()!.demandeBrouillonEnCoursDeReprise
            && !this.demandePermisVisiteService.getDemandeIncomplete()) {
            // L'utilisateur est sur une reprise et souhaite enregistrer les données saisies
            this.gestionDuCasDeLaDemandeBrouillon(true);
        } else if (this.demandePermisVisiteService.isDemandeNonNull()
            && this.demandePermisVisiteService.getDemande()!.id !== undefined
            && !this.demandePermisVisiteService.getDemande()!.demandeBrouillonEnCoursDeReprise
            && !this.demandePermisVisiteService.getDemandeIncomplete()) {
            // L'utilisateur est sur la première saisie de sa demande qui a déjà été enregistrée en BDD et souhaite enregistrer les données saisies
            this.gestionDuCasDeLaDemandeBrouillon(true);
        } else {
            // L'utilisateur est sur la première saisie de sa demande qui n'a pas encore été enregistrée (Répondre oui est l'acceptation de la perte d'information saisies)
            this.sortiePopIn();
        }
    }

    /**
     * Methode permettant de gérer le cas de la demande brouillon
     * lorsque l'utilisateur souhaite quitter le tunnel
     */
    gestionDuCasDeLaDemandeBrouillon(enregistrementDesDonneesSaisies: boolean) {
        // Si l'utilisateur souhaite enregistrer les données saisies
        if (enregistrementDesDonneesSaisies) {
            // On persiste l'objet portant les modifications saisies
            if (this.demandePermisVisiteService.getDemande()!.informationVisiteur!.dateNaissance) {
                const date = formatDate(this.demandePermisVisiteService.getDemande()!.informationVisiteur!.dateNaissance!, 'yyyy-MM-dd', 'fr-FR');
                this.demandePermisVisiteService.getDemande()!.informationVisiteur!.dateNaissance = new Date(date);
            }
            this.demandePermisVisiteService.majSortiPopin(this.alimenterMajSortiPopin(this.demandePermisVisiteService.getDemande()!))
                .subscribe({
                    next: _ => {
                        this.sortiePopIn();
                    },
                    error: () => {
                        this.alertService.error('error.internalServer');
                    },
                });
        } else {
            // On persiste la demande dans l'état initial
            this.demandePermisVisiteService.majSortiPopin(this.alimenterMajSortiPopin(this.demandePermisVisiteService.getDemandeBddPourRollBack()!))
                .subscribe({
                    next: _ => {
                        this.sortiePopIn();
                    },
                    error: () => {
                        this.alertService.error('error.internalServer');
                    },
                });
        }
    }

    alimenterMajSortiPopin(dpv: IDemandePermisVisite) {
        const majDemand = new MajDemandePermisVisiteSortiParcours();
        majDemand.id = dpv.id;
        majDemand.demandePersonnelle = dpv.demandePersonnelle;
        majDemand.numeroEtape = dpv.numeroEtape;
        majDemand.informationDetenu = dpv.informationDetenu;
        majDemand.informationVisiteur = dpv.informationVisiteur;
        majDemand.pieceJustificatives = dpv.pieceJustificatives;

        return majDemand;
    }

    /**
     * Méthode permettant d'effacer les demandes présentes dans le service
     * à la sortie du tunnel
     */
    sortiePopIn() {
        // En sortie de tunnel par la popin de sortie, on reset l'objet Demande dans le sessionStorage pour sécuriser
        this.localService.removeData('objectDemande');
        // On efface la demande du service
        this.demandePermisVisiteService.clearDemande();
        this.demandePermisVisiteService.clearDemandeBddPourRollBack();

        // Dans le cas d'une demande incomplète, on réinitialise les informations stockées dans le service
        if (this.demandePermisVisiteService.getDemandeIncomplete()) {
            this.navigationTunnelService.reinitialisationDonneesDemandeIncomplete();
        }

        // On ferme la pop in
        this.close(true);
    }
}
